import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetPaymentStatusQuery,
  useGetPaymentUrlMutation,
} from "@/services/api/entities/payment";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { FlowType, paymentActions, paymentSelectors } from "@/features/payment-form";
import { PaymentStatus } from "@/services/api/entities/payment/types";
import { appRouterPaths } from "@/services/navigation";
import { signUpActions, SignUpStep } from "@/features/sign-up";
import { POLLING_INTERVAL } from "../constants";

export const useCheckPayment = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const paymentOperationId = useAppSelector(paymentSelectors.selectPaymentOperationId);
  const flowType = useAppSelector(paymentSelectors.selectFlowType);
  const getPaymentUrlPayload = useAppSelector(paymentSelectors.selectPaymentUrlPayload);
  const paymentUrlHasError = useAppSelector(paymentSelectors.selectPaymentUrlHasError);
  const shouldSkipPolling = useAppSelector(paymentSelectors.selectShouldSkipPolling);
  const paymentUrl = useAppSelector(paymentSelectors.selectPaymentUrl);

  const [getPaymentUrl] = useGetPaymentUrlMutation();

  const { data, isFetching } = useGetPaymentStatusQuery(
    { paymentOperationId },
    { skip: shouldSkipPolling, pollingInterval: POLLING_INTERVAL },
  );

  const handleSuccessPayment = () => {
    switch (flowType) {
      case FlowType.SIGN_UP:
        dispatch(signUpActions.goToStep(SignUpStep.PinCode));
        navigate(appRouterPaths.signUp);
        break;
      case FlowType.UPDATE_SUBSCRIPTION:
      case FlowType.RENEW_SUBSCRIPTION:
        navigate(appRouterPaths.enterPin);
        break;
      case FlowType.MOBILE_PAYMENT:
        dispatch(signUpActions.goToStep(SignUpStep.SuccessPayment));
        navigate(appRouterPaths.signUp);
        break;
    }
    dispatch(paymentActions.resetPaymentState());
  };

  useEffect(() => {
    if (data?.state === PaymentStatus.PAID || data?.state === PaymentStatus.CANCELED) {
      if (data?.state === PaymentStatus.PAID) {
        handleSuccessPayment();
      }
    }
  }, [data, isFetching]);

  const handleTryAgain = useCallback(() => {
    if (!getPaymentUrlPayload) {
      return;
    }

    dispatch(paymentActions.resetCheckPaymentRetryCount());

    if (!paymentUrlHasError && paymentUrl) {
      dispatch(paymentActions.setCheckPaymentPolling(true));
      navigate(appRouterPaths.checkPayment);

      setTimeout(() => {
        window.location.href = paymentUrl;
      }, 100);

      return;
    }

    dispatch(paymentActions.resetPaymentUrlError());

    getPaymentUrl(getPaymentUrlPayload)
      .unwrap()
      .then((res) => {
        dispatch(paymentActions.setPaymentOperationId(res.paymentOperationId));
        dispatch(paymentActions.setIdempotenceKey(res.idempotenceKey));
        dispatch(paymentActions.setCheckPaymentPolling(true));
        navigate(appRouterPaths.checkPayment);

        setTimeout(() => {
          window.location.href = res.paymentUrl;
        }, 100);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [dispatch, getPaymentUrlPayload, paymentUrlHasError, paymentUrl, navigate]);

  const handleGoBack = useCallback(() => {
    dispatch(paymentActions.resetCheckPaymentRetryCount());
    dispatch(paymentActions.resetPaymentUrlError());

    if (flowType === FlowType.SIGN_UP || flowType === FlowType.MOBILE_PAYMENT) {
      dispatch(signUpActions.goToStep(SignUpStep.Payment));
      navigate(appRouterPaths.signUp);
    } else {
      navigate(appRouterPaths.checkStatus);
    }
  }, [navigate, flowType, dispatch]);

  return {
    state: {
      paymentCancelled:
        data?.state === PaymentStatus.CANCELED || paymentUrlHasError || shouldSkipPolling,
    },
    methods: {
      handleTryAgain,
      handleGoBack,
    },
  };
};
